import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCatalogBrands, getCatalogProducts } from "@/lib/api-client";
import { parseListingSearchParams } from "@/lib/listing-query";
import { ListingControls } from "@/components/product/ListingControls";
import { LoadingBlock, ErrorBlock } from "@/components/product/QueryFeedback";
import { NotFoundPage } from "@/pages/shop/NotFoundPage";

/**
 * Équivalent de apps/web/app/(shop)/marques/[slug]/page.tsx. Il n'existe pas
 * de `getBrandBySlug()` côté API client : on retrouve la marque correspondante
 * dans `getCatalogBrands()` (pour le nom d'affichage) et on filtre
 * `getCatalogProducts({ brand: slug })` pour ses produits — deux requêtes,
 * comme l'original.
 */
export function MarqueDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const filters = parseListingSearchParams(searchParams);

  const brandsQuery = useQuery({
    queryKey: ["catalog", "brands"],
    queryFn: getCatalogBrands,
  });
  const brand = brandsQuery.data?.find((b) => b.slug === slug);

  const productsQuery = useQuery({
    queryKey: ["catalog", "products", "brand", slug, filters],
    queryFn: () => getCatalogProducts({ ...filters, brand: slug }),
    enabled: slug.length > 0,
  });

  if (brandsQuery.isLoading || productsQuery.isLoading) return <LoadingBlock />;
  if (brandsQuery.isError || productsQuery.isError || !productsQuery.data) return <ErrorBlock />;
  if (!brand) return <NotFoundPage />;

  const products = productsQuery.data;
  const hasResults = products.items.length > 0;
  const total = products.facets.availability.inStock + products.facets.availability.outOfStock;

  return (
    <div className="container-vvs py-4">
      <nav aria-label="Fil d'Ariane" className="mb-3 flex flex-wrap gap-1 font-body text-xs text-muted">
        <Link to="/marques" className="hover:text-navy">
          Marques
        </Link>
        <span aria-hidden="true">/</span>
        <span className="text-ink">{brand.name}</span>
      </nav>

      <header className="mb-4">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">{brand.name}</h1>
        {brand.countryOfOrigin ? (
          <p className="mt-2 font-body text-sm text-muted">Origine : {brand.countryOfOrigin}</p>
        ) : null}
      </header>

      {hasResults ? (
        <ListingControls
          facets={products.facets}
          total={total}
          initialItems={products.items}
          initialNextCursor={products.nextCursor}
          query={{ ...filters, brand: slug }}
        />
      ) : (
        <div className="py-16 text-center">
          <p className="font-body text-base text-ink">Aucun produit disponible pour cette marque pour le moment.</p>
          <p className="mt-2 font-body text-sm text-muted">
            Découvrez{" "}
            <Link to="/marques" className="text-navy underline">
              nos autres marques
            </Link>
            .
          </p>
        </div>
      )}
    </div>
  );
}
